import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { services } from "@/data/content";
import useSeo from "@/hooks/useSeo";

export default function ServiceDetail() {
    const { slug } = useParams();
    const service = services.find((s) => s.slug === slug);

    useSeo({
        title: service ? `${service.title} — ${service.tagline}` : "Services",
        description: service?.summary,
        path: service ? `/services/${service.slug}` : "/services",
    });

    if (!service) return <Navigate to="/services" replace />;

    const Icon = service.icon;

    return (
        <div data-testid="service-detail-page" className="bg-white">
            <section className="relative pt-16 pb-24 bg-slate-50 border-b border-slate-200 overflow-hidden">
                <div className="absolute inset-0 bg-grid-slate opacity-40" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link
                        to="/services"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" /> All services
                    </Link>

                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 rounded-2xl bg-white shadow-sm border border-slate-200 flex items-center justify-center">
                            <Icon className="w-7 h-7 text-blue-600" />
                        </div>
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">
                            {service.tagline}
                        </div>
                    </div>

                    <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black text-slate-900 tracking-tighter leading-[1.08] sm:leading-[0.95] max-w-4xl">
                        {service.title}
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
                        {service.description}
                    </p>
                </div>
            </section>

            <section className="py-24 sm:py-32">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-7 rounded-3xl overflow-hidden aspect-[4/3]"
                    >
                        <img
                            src={service.image}
                            alt={service.title}
                            className="w-full h-full object-cover"
                        />
                    </motion.div>

                    <div className="lg:col-span-5">
                        <h2 className="font-display text-3xl font-black text-slate-900 tracking-tighter mb-6">
                            What&apos;s included
                        </h2>
                        <ul className="space-y-4">
                            {service.highlights.map((h) => (
                                <li
                                    key={h}
                                    className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200"
                                >
                                    <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                                    <span className="text-slate-800">{h}</span>
                                </li>
                            ))}
                        </ul>

                        <Link
                            to={`/quote?service=${service.slug}`}
                            data-testid="service-cta-quote"
                            className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-slate-900 text-white font-semibold hover:bg-blue-600 transition-colors"
                        >
                            Get a quote for this service
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
